import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { validateRedirectUrl, logValidationResult, getSupabaseConfigInstructions } from '../../utils/redirectUrlValidator';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!email) {
      setError('Email address is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Sending password reset email to:', email);

      // CRITICAL: Use the exact URL format that matches Supabase whitelist
      // The URL MUST be whitelisted in: Supabase Dashboard > Authentication > URL Configuration > Redirect URLs
      // For production: Use app.pathwayoz.com domain
      // For development: Use localhost origin
      const isLocalhost = window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1';
      const redirectUrl = isLocalhost 
        ? `${window.location?.origin}/password-reset-page`
        : 'https://app.pathwayoz.com/password-reset-page';
      
      console.log('Using redirect URL:', redirectUrl);
      console.log('Current origin:', window.location?.origin);
      console.log('Is localhost:', isLocalhost);
      console.log('Expected whitelisted URLs:');
      console.log('  - http://localhost:5173/password-reset-page');
      console.log('  - http://localhost:3000/password-reset-page');
      console.log('  - https://your-production-domain.com/password-reset-page');

      // Validate redirect URL before making the API call
      const validation = validateRedirectUrl(redirectUrl, 'password reset');
      logValidationResult(validation, redirectUrl, 'password reset');
      
      // Show warnings to user if any
      if (validation?.warnings?.length > 0) {
        console.warn('⚠️ Redirect URL validation warnings detected. Proceeding with caution...');
      }
      
      // If validation fails completely, show error immediately
      if (!validation?.isValid) {
        setError(
          `Invalid redirect URL configuration: ${validation?.error}. ` +
          'Please contact your administrator.'
        );
        setIsLoading(false);
        return;
      }

      // IMPORTANT: The redirectTo parameter will ONLY work if the URL is whitelisted
      // If not whitelisted, Supabase will fall back to the Site URL setting
      const { data, error: resetError } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (resetError) {
        console.error('Password reset error:', resetError);
        
        // Check for email service configuration errors (most common issue)
        if (resetError?.message?.toLowerCase()?.includes('error sending') ||
            resetError?.message?.toLowerCase()?.includes('recovery email') ||
            resetError?.message?.toLowerCase()?.includes('smtp') || 
            resetError?.message?.toLowerCase()?.includes('email') ||
            resetError?.message?.toLowerCase()?.includes('mail service') ||
            resetError?.message?.toLowerCase()?.includes('email not configured')) {
          setError(
            'Email service is not configured. Please contact your administrator to configure email settings in Supabase Dashboard.'
          );
          console.error('❌ EMAIL SERVICE NOT CONFIGURED');
          console.error('Administrator Action Required:');
          console.error('1. Go to Supabase Dashboard > Project Settings > Auth > SMTP Settings');
          console.error('2. Enable Custom SMTP or use Supabase built-in email service');
          console.error('3. Go to Authentication > Email Templates');
          console.error('4. Enable and customize the "Reset Password" template');
          console.error('5. Ensure redirect URL is in allowed redirect URLs list');
          console.error('Error details:', resetError);
        } else if (resetError?.message?.toLowerCase()?.includes('redirect') || 
            resetError?.message?.toLowerCase()?.includes('url') ||
            resetError?.message?.toLowerCase()?.includes('not allowed')) {
          const instructions = getSupabaseConfigInstructions(redirectUrl);
          setError(
            `Configuration Error: The redirect URL (${redirectUrl}) is not whitelisted in Supabase. ` +
            'Please contact your administrator to add this URL to the allowed redirect URLs list in ' + 'Supabase Dashboard > Authentication > URL Configuration.'
          );
          console.error('❌ REDIRECT URL NOT WHITELISTED');
          console.error('Required Action: Add the following URL to Supabase allowed redirect URLs:');
          console.error(`  → ${redirectUrl}`);
          console.error('Steps:');
          instructions?.steps?.forEach(step => console.error(step));
        } else {
          setError('Unable to send password reset email. Please try again or contact support.');
          console.error('Password reset error:', resetError);
        }
        setIsLoading(false);
        return;
      }

      console.log('✅ Password reset email sent successfully');
      console.log('📧 User should receive email with link to:', redirectUrl);
      console.log('⚠️  If email link goes to wrong page, check:');
      console.log('   1. Supabase Dashboard > Authentication > URL Configuration > Redirect URLs');
      console.log(`   2. Ensure this exact URL is whitelisted: ${redirectUrl}`);
      console.log('   3. Supabase Dashboard > Project Settings > General > Site URL');
      console.log('   4. Site URL should be your app origin, not a specific page');
      
      setSuccessMessage(
        'Password reset link sent! Please check your email inbox (and spam folder) for instructions to reset your password.'
      );
      setEmail('');

      // Auto-close modal after 3 seconds on success
      setTimeout(() => {
        onClose?.();
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Unexpected password reset error:', err);
      setError('An unexpected error occurred. Please contact support or try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEmail('');
    setError('');
    setSuccessMessage('');
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Lock" size={20} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Reset Password</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancel}
            disabled={isLoading}
            className="h-8 w-8"
          >
            <Icon name="X" size={18} />
          </Button>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-6">
          Enter your email address and we'll send you a secure link to reset your password.
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Email Address"
            type="email"
            placeholder="Enter your registered email"
            value={email}
            onChange={(e) => {
              setEmail(e?.target?.value);
              setError('');
            }}
            error={error}
            required
            autoComplete="email"
            disabled={isLoading}
          />

          {/* Success Message */}
          {successMessage && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success mt-0.5" />
                <span className="text-sm text-success">{successMessage}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoading}
              disabled={isLoading || !!successMessage}
              iconName="Send"
              iconPosition="left"
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </div>
        </form>

        {/* Back to Login */}
        <div className="mt-6 pt-4 border-t border-border text-center">
          <Button
            variant="link"
            onClick={handleCancel}
            disabled={isLoading}
            className="text-sm"
          >
            Back to Login
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;